import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  data?: Record<string, unknown>;
  requireInteraction?: boolean;
}

interface UseNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission | 'default';
  requestPermission: () => Promise<boolean>;
  sendNotification: (options: NotificationOptions) => void;
  scheduleReminder: (options: NotificationOptions, delayMs: number) => NodeJS.Timeout;
  cancelReminder: (timerId: NodeJS.Timeout) => void;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission | 'default'>('default');
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
    }
  }, [isSupported]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      toast.error('Notifications are not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Notifications enabled! You\'ll receive reminders for job applications.');
        return true;
      } else if (result === 'denied') {
        toast.error('Notification permission denied. You can enable it in browser settings.');
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to request notification permission');
      return false;
    }
  }, [isSupported]);

  const sendNotification = useCallback((options: NotificationOptions) => {
    if (!isSupported || permission !== 'granted') {
      // Fallback to toast notification
      toast(options.title, {
        description: options.body,
      });
      return;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/pwa-192x192.png',
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction || false,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Navigate based on notification type
        if (options.data?.url) {
          window.location.href = options.data.url as string;
        }
      };
    } catch (error) {
      console.error('Error sending notification:', error);
      // Fallback to toast
      toast(options.title, {
        description: options.body,
      });
    }
  }, [isSupported, permission]);

  const scheduleReminder = useCallback((options: NotificationOptions, delayMs: number): NodeJS.Timeout => {
    return setTimeout(() => {
      sendNotification(options);
    }, delayMs);
  }, [sendNotification]);

  const cancelReminder = useCallback((timerId: NodeJS.Timeout) => {
    clearTimeout(timerId);
  }, []);

  return {
    isSupported,
    permission,
    requestPermission,
    sendNotification,
    scheduleReminder,
    cancelReminder,
  };
};

// Utility functions for common notification types
export const notificationTemplates = {
  interviewReminder: (companyName: string, jobTitle: string, time: string) => ({
    title: `Interview Reminder: ${companyName}`,
    body: `Your interview for ${jobTitle} is coming up at ${time}. Good luck!`,
    tag: 'interview-reminder',
    requireInteraction: true,
    data: { url: '/job-tracker', type: 'interview' },
  }),

  applicationDeadline: (companyName: string, jobTitle: string, daysLeft: number) => ({
    title: `Application Deadline: ${companyName}`,
    body: daysLeft === 0 
      ? `Today is the last day to apply for ${jobTitle}!`
      : `Only ${daysLeft} day${daysLeft > 1 ? 's' : ''} left to apply for ${jobTitle}`,
    tag: 'deadline-reminder',
    requireInteraction: true,
    data: { url: '/job-tracker', type: 'deadline' },
  }),

  followUpReminder: (companyName: string, jobTitle: string) => ({
    title: `Follow-up Reminder: ${companyName}`,
    body: `It's been a week since you applied for ${jobTitle}. Consider sending a follow-up!`,
    tag: 'followup-reminder',
    data: { url: '/job-tracker', type: 'followup' },
  }),

  resumeUpdateReminder: () => ({
    title: 'Time to Update Your Resume',
    body: 'Keep your resume fresh! Consider adding recent achievements or skills.',
    tag: 'resume-update',
    data: { url: '/dashboard', type: 'resume' },
  }),
};
